/**
 * The Reviews screen's 'Quality flags' table (admin doc 20, the design's second section): open
 * flags by pandit and type. Support raises them; supply decides on them in the directory, so the
 * last column is the action the pandit already carries — never a proposal.
 */
import type { Tone } from '../components/primitives';
import type { QualityFlagGroup } from '../lib/api/hooks/reviews';

export interface FlagRow {
  id: string;
  pandit: string;
  kind: string;
  src: string;
  count: number;
  action: string;
  tone: Tone;
  /** The server's advisory (`suggest_escalation`): open flags have reached the type's threshold. */
  escalate: string | null;
}

/** Mildest first, as the server's `QualityActionType` declares them. */
const STANDING: Record<string, { label: string; tone: Tone }> = {
  warning: { label: 'Warning', tone: 'info' },
  training_required: { label: 'Training required', tone: 'warn' },
  temporary_suspension: { label: 'Suspended', tone: 'error' },
  delisting: { label: 'Delisted', tone: 'error' },
};

function words(code: string): string {
  const spaced = code.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Where to go to read what happened — the newest open flag's origin, most specific first. */
export function flagSource(g: QualityFlagGroup): string {
  if (g.dispute_id !== null) return `Dispute ${g.dispute_id}`;
  if (g.booking_code !== null) return `Booking ${g.booking_code}`;
  if (g.review_id !== null) return `Review ${g.review_id}`;
  return words(g.source);
}

export function flagRows(groups: readonly QualityFlagGroup[]): FlagRow[] {
  return groups.map((g) => {
    const standing = g.standing_action
      ? STANDING[g.standing_action]
      : undefined;
    return {
      id: `${g.pandit_id}:${g.flag_type}`,
      pandit: g.pandit_name,
      kind: words(g.flag_type),
      src: flagSource(g),
      count: g.window_count,
      action:
        standing?.label ??
        (g.standing_action ? words(g.standing_action) : 'None'),
      tone: standing?.tone ?? 'neutral',
      escalate: g.suggest_escalation
        ? `${g.open_count} open · threshold ${g.escalation_threshold}`
        : null,
    };
  });
}
